import {useCallback, useEffect, useState} from 'react';

const R2_PUBLIC_URL = 'https://pub-d32d091f07724fc9803607ac00091b73.r2.dev';
const CATALOG_URL = `${R2_PUBLIC_URL}/catalog.json`;
const CATALOG_CACHE_KEY = 'lavita-podcast-catalog-v1';

const resolveMediaUrl = (url) => new URL(url, R2_PUBLIC_URL).toString();

// Episodes hosted in this repo (served from the site itself) rather than the R2
// catalog. Each is inserted at the top of its category; `featured: true` promotes
// it to the hero slot on the Podcast page.
const LOCAL_EPISODES = [
    {
        id: 'mrna-cancer-vaccines',
        title: 'The Promises and The Challenges of mRNA Cancer Vaccines',
        categoryTitle: 'Personalized Cancer Therapies',
        audioUrl: '/podcast/mrna-cancer-vaccines/audio.m4a',
        artworkUrl: '/podcast/mrna-cancer-vaccines/cover.png',
        featured: true,
    },
];

const injectLocalEpisodes = (categories) => {
    LOCAL_EPISODES.forEach((local) => {
        const episode = {
            id: local.id,
            title: local.title,
            topic: local.categoryTitle,
            audioUrl: local.audioUrl,
            artworkUrl: local.artworkUrl,
            featured: !!local.featured,
        };
        const category = categories.find((c) => c.title === local.categoryTitle);
        if (category) {
            category.episodes = [episode, ...category.episodes.filter((e) => e.id !== episode.id)];
        } else {
            categories.push({id: local.categoryTitle, title: local.categoryTitle, episodes: [episode]});
        }
    });
    return categories;
};

const normalizeCatalog = (catalog) => {
    if (!Array.isArray(catalog?.categories)) {
        throw new Error('Podcast catalog did not include any categories');
    }

    const categories = catalog.categories.map((category) => ({
        id: category.id,
        title: category.title || 'Lavita AI',
        episodes: Array.isArray(category.episodes) ? category.episodes
            .filter((episode) => episode?.id && episode?.audio?.url)
            .map((episode) => ({
                id: episode.id,
                title: episode.title || 'Untitled episode',
                topic: category.title || 'Lavita AI',
                audioUrl: resolveMediaUrl(episode.audio.url),
                artworkUrl: episode.cover?.url ? resolveMediaUrl(episode.cover.url) : null,
            })) : [],
    })).filter((category) => category.episodes.length > 0);

    return injectLocalEpisodes(categories);
};

const getCachedCatalog = () => {
    try {
        const cachedCatalog = window.localStorage.getItem(CATALOG_CACHE_KEY);
        return cachedCatalog ? normalizeCatalog(JSON.parse(cachedCatalog)) : null;
    } catch {
        return null;
    }
};

const cacheCatalog = (catalog) => {
    try {
        window.localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(catalog));
    } catch {
        // The catalog remains available for this session when storage is unavailable.
    }
};

const getCatalogError = (status) => {
    if (status === 404) {
        return 'Podcast catalog is temporarily unavailable.';
    }

    if (status === 429) {
        return 'Podcast catalog is busy right now. Please try again shortly.';
    }

    return `Catalog request failed: ${status}`;
};

const usePodcasts = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPodcasts = useCallback(async (signal) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(CATALOG_URL, {signal});
            if (!response.ok) {
                throw new Error(getCatalogError(response.status));
            }

            const catalog = await response.json();
            const normalizedCatalog = normalizeCatalog(catalog);
            cacheCatalog(catalog);
            setCategories(normalizedCatalog);
        } catch (requestError) {
            if (requestError.name !== 'AbortError') {
                const cachedCatalog = getCachedCatalog();
                if (cachedCatalog) {
                    setCategories(cachedCatalog);
                    setError('Showing the last available podcast catalog.');
                } else {
                    setError(requestError.message || 'Unable to load podcasts right now. Please try again.');
                }
            }
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchPodcasts(controller.signal);

        return () => controller.abort();
    }, [fetchPodcasts]);

    const refresh = useCallback(() => fetchPodcasts(), [fetchPodcasts]);

    return {categories, loading, error, refresh};
};

export default usePodcasts;
